import React, { useState, useEffect } from 'react';
import { Card, Table, Typography, Tag, Space, Button, Tabs, message, Popconfirm } from 'antd';
import { CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { monitorAPI } from '../services/api';
import { formatUTCToBeijing } from '../utils/time';

const { Title, Text } = Typography;

const AdminMonitor = () => {
  const [loginSessions, setLoginSessions] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('sessions');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchLoginSessions(),
        fetchAlerts()
      ]);
    } catch (error) {
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchLoginSessions = async () => {
    try {
      const response = await monitorAPI.getLoginSessions(0, 100);
      setLoginSessions(response.data);
    } catch (error) {
      console.error('获取登录记录失败:', error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await monitorAPI.getAlerts(0, 100);
      setAlerts(response.data);
    } catch (error) {
      console.error('获取报警记录失败:', error);
    }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      await monitorAPI.resolveAlert(alertId);
      message.success('报警已解决');
      fetchAlerts();
    } catch (error) {
      message.error('解决失败');
    }
  };

  const sessionColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 120,
    },
    {
      title: '认证方式',
      dataIndex: 'auth_method',
      key: 'auth_method',
      width: 120,
      render: (method) => {
        const methodMap = {
          'PASSWORD': '密码登录',
          'ACTIVATION_CODE': '激活码登录',
          'REFRESH_TOKEN': '刷新令牌',
        };
        return methodMap[method] || method;
      },
    },
    {
      title: 'IP地址',
      dataIndex: 'remote_ip',
      key: 'remote_ip',
      width: 140,
      render: (ip) => ip || '-',
    },
    {
      title: '登录时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text) => text ? formatUTCToBeijing(text) : '-',
    },
    {
      title: '最后活跃',
      dataIndex: 'last_seen_at',
      key: 'last_seen_at',
      width: 180,
      render: (text) => text ? formatUTCToBeijing(text) : '-',
    },
    {
      title: '过期时间',
      dataIndex: 'expires_at',
      key: 'expires_at',
      width: 180,
      render: (text) => text ? formatUTCToBeijing(text) : '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const color = status === '活跃' ? 'green' : 'red';
        return <Tag color={color}>{status}</Tag>;
      },
    },
  ];

  const alertColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '类型',
      dataIndex: 'alert_type',
      key: 'alert_type',
      width: 140,
    },
    {
      title: '严重程度',
      dataIndex: 'severity',
      key: 'severity',
      width: 100,
      render: (severity) => {
        const colorMap = {
          'INFO': 'blue',
          'WARNING': 'orange',
          'ERROR': 'red',
          'CRITICAL': 'red',
        };
        return <Tag color={colorMap[severity] || 'default'}>{severity}</Tag>;
      },
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 200,
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 300,
      ellipsis: true,
    },
    {
      title: '相关用户',
      dataIndex: 'username',
      key: 'username',
      width: 120,
      render: (username) => username || '-',
    },
    {
      title: 'IP地址',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 140,
      render: (ip) => ip || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text) => text ? formatUTCToBeijing(text) : '-',
    },
    {
      title: '状态',
      dataIndex: 'resolved',
      key: 'resolved',
      width: 100,
      render: (resolved) => {
        const color = resolved ? 'green' : 'red';
        const text = resolved ? '已解决' : '未解决';
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        !record.resolved && (
          <Popconfirm
            title="确定要解决此报警吗？"
            onConfirm={() => handleResolveAlert(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              icon={<CheckCircleOutlined />}
              size="small"
            >
              解决
            </Button>
          </Popconfirm>
        )
      ),
    },
  ];

  const tabItems = [
    {
      key: 'sessions',
      label: (
        <Space>
          登录记录
          <Tag color="blue">{loginSessions.length}</Tag>
        </Space>
      ),
      children: (
        <Table
          columns={sessionColumns}
          dataSource={loginSessions}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1200 }}
        />
      ),
    },
    {
      key: 'alerts',
      label: (
        <Space>
          报警记录
          <Tag color="red">{alerts.filter(a => !a.resolved).length}</Tag>
        </Space>
      ),
      children: (
        <Table
          columns={alertColumns}
          dataSource={alerts}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1500 }}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', background: '#0d1117', color: '#e6edf3' }}>
      <Card
        title="系统监控"
        style={{
          background: '#161b22',
          border: '1px solid #30363d',
          color: '#e6edf3'
        }}
        headStyle={{
          color: '#e6edf3',
          borderBottom: '1px solid #30363d'
        }}
        extra={
          <Button type="primary" onClick={fetchData} loading={loading}>
            刷新
          </Button>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          style={{ color: '#e6edf3' }}
        />
      </Card>
    </div>
  );
};

export default AdminMonitor;
